import React, { MutableRefObject } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import Typography from '@mui/material/Typography'
import { RootActionType, RootState } from 'duck'
import { ReduxProps } from 'utils/Types'
import Button from 'components/Button'
import { actions, selectors, STEP } from 'containers/NFTSellPanel/duck'
import { useBreakpoint } from 'utils/Hooks'
import CoverSelection from './CoverSelection'
import ImageSelection from './ImageSelection'
import DialogImageSelection from './DialogImageSelection'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from '../../NFTSellForm.module.scss'

export const GRID_PROJECTS_CONFIG = {
  xs: {
    columns: 2,
    paddingX: 16,
    gutter: 24
  },
  sm: {
    paddingX: 24
  },
  default: {
    defaultColumnWidth: 112,
    rowHeight: 300,
    square: true,
    gutter: 36,
    paddingTop: 24,
    paddingBottom: 72,
    paddingX: 72
  }
}

const mapStateToProps = (state: RootState) => ({
  gridCount: selectors.gridCount(state),
  step: selectors.step(state),
  gridLoading: selectors.gridLoading(state),
  selectedGridCount: selectors.selectedGridCount(state),
  appliedSelectedGridCount: selectors.appliedSelectedGridCount(state),
  isSelectedImageEnough: selectors.isSelectedImageEnough(state),
  thumbnail: selectors.formData(state).thumbnail
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      setStep: actions.setStep
    },
    dispatch
  )

type ImagesListProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps> & {
  dialogBodyRef?: MutableRefObject<any>
}

const ImagesList: React.FC<ImagesListProps> = props => {
  const {
    setStep,
    dialogBodyRef,
    appliedSelectedGridCount,
    gridLoading,
    step,
    isSelectedImageEnough,
    thumbnail
  } = props

  const { isSmallscreenAbove } = useBreakpoint()

  return (
    <div className={clsx('flex h-1/100 min-h-0 flex-auto flex-col', isSmallscreenAbove && 'pt-3')}>
      <div
        className={clsx(
          'w-full sm:flex sm:items-center',
          !isSmallscreenAbove && 'pt-1',
          isSmallscreenAbove ? 'pb-8' : 'pb-9',
          styles.ContainerImagesList
        )}
      >
        <div
          className={
            isSmallscreenAbove
              ? clsx(
                  'min-h-0 w-1/100 flex-auto',
                  step === STEP.CURATE_COLLECTION && stylesGlobal.TextWhite38
                )
              : undefined
          }
        >
          {isSmallscreenAbove ? (
            <>
              <Typography variant="h5" className="mb-3">
                Tap an image to select a cover image for your Art Mine listing
              </Typography>

              <Typography variant="body1">
                Or edit your collection to choose what will be for sale.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h5" className="mb-6">
                {`You have ${appliedSelectedGridCount} images included in your NFT Project`}
              </Typography>

              <Typography variant="body1">
                Tap on an image to select a cover image for your Art Mine listing (required)
              </Typography>

              <br />

              <Typography variant="body1">
                Or edit your collection to choose what will be for sale.
              </Typography>
            </>
          )}
        </div>

        {isSmallscreenAbove ? (
          <Button
            className={clsx('ml-6', styles.ButtonImagesListAction)}
            color="secondary"
            variant="contained"
            onClick={() => {
              setStep(STEP.DETAILS)
            }}
            size="small"
            disabled={
              !Boolean(thumbnail) ||
              !isSelectedImageEnough ||
              Boolean(gridLoading) ||
              step === STEP.CURATE_COLLECTION
            }
            fullWidth
          >
            {gridLoading ? `Loading Image...` : `CONTINUE`}
          </Button>
        ) : null}
      </div>

      {(isSmallscreenAbove && step === STEP.SELECT_COVER) || !isSmallscreenAbove ? (
        <CoverSelection dialogBodyRef={dialogBodyRef} />
      ) : null}

      {isSmallscreenAbove && step === STEP.CURATE_COLLECTION ? <ImageSelection /> : null}

      {!isSmallscreenAbove ? <DialogImageSelection open={step === STEP.CURATE_COLLECTION} /> : null}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ImagesList)
